/**
 * KnowledgeForm
 *
 * Form for creating and editing domain knowledge entries.
 *
 * Part of Story 30: Domain Knowledge Refinement System
 *
 * Date: December 18, 2025
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from '@/components/ui/use-toast';
import { Loader2 } from 'lucide-react';
import { useSite } from '@/contexts/SiteContext';
import {
  DomainKnowledge,
  KnowledgeType,
  ScopeLevel,
  ResearchMethodology,
  createKnowledge,
  updateKnowledge,
} from '@/services/domainKnowledgeService';

interface KnowledgeFormProps {
  open: boolean;
  onClose: () => void;
  knowledge?: DomainKnowledge | null;
  onSaved: () => void;
}

const KNOWLEDGE_TYPES: { value: KnowledgeType; label: string; description: string }[] = [
  { value: 'terminology', label: 'Terminology', description: 'Correct terms, naming conventions' },
  { value: 'specification', label: 'Specification', description: 'Technical specs, measurements' },
  { value: 'context', label: 'Context', description: 'Industry or domain context' },
  { value: 'expertise_signal', label: 'Expertise Signal', description: 'Expert knowledge markers' },
  { value: 'validation_rule', label: 'Validation Rule', description: 'Rules for content validation' },
];

const SCOPE_LEVELS: { value: ScopeLevel; label: string; description: string }[] = [
  { value: 'sector', label: 'Sector', description: 'Applies to entire sector' },
  { value: 'category', label: 'Category', description: 'Applies to category tree' },
  { value: 'brand', label: 'Brand', description: 'Applies to specific brand' },
  { value: 'attribute', label: 'Attribute', description: 'Applies to product attribute' },
  { value: 'product', label: 'Product', description: 'Applies to specific product' },
];

const RESEARCH_METHODOLOGIES: { value: ResearchMethodology; label: string }[] = [
  { value: 'WEB_SEARCH', label: 'Web Search' },
  { value: 'MANUFACTURER_SPECS', label: 'Manufacturer Specs' },
  { value: 'INDUSTRY_STANDARDS', label: 'Industry Standards' },
  { value: 'EXPERT_INPUT', label: 'Expert Input (Manual)' },
];

export function KnowledgeForm({ open, onClose, knowledge, onSaved }: KnowledgeFormProps) {
  const { selectedSite } = useSite();
  const [loading, setLoading] = useState(false);
  const isEdit = !!knowledge;

  const [formData, setFormData] = useState({
    title: '',
    knowledge_text: '',
    knowledge_type: 'context' as KnowledgeType,
    scope_level: 'sector' as ScopeLevel,
    scope_sector_id: '',
    scope_category_path: '',
    scope_brand: '',
    scope_attribute_name: '',
    scope_product_id: '',
    research_question: '',
    research_methodology: 'WEB_SEARCH' as ResearchMethodology,
  });

  // Reset form when opening or knowledge changes
  useEffect(() => {
    if (knowledge) {
      setFormData({
        title: knowledge.title || '',
        knowledge_text: knowledge.knowledgeText || '',
        knowledge_type: knowledge.knowledgeType || 'context',
        scope_level: knowledge.scopeLevel || 'sector',
        scope_sector_id: knowledge.scopeSectorId?.toString() || '',
        scope_category_path: knowledge.scopeCategoryPath || '',
        scope_brand: knowledge.scopeBrand || '',
        scope_attribute_name: knowledge.scopeAttributeName || '',
        scope_product_id: knowledge.scopeProductId || '',
        research_question: knowledge.researchQuestion || '',
        research_methodology: knowledge.researchMethodology || 'WEB_SEARCH',
      });
    } else {
      setFormData({
        title: '',
        knowledge_text: '',
        knowledge_type: 'context',
        scope_level: 'sector',
        scope_sector_id: '',
        scope_category_path: '',
        scope_brand: '',
        scope_attribute_name: '',
        scope_product_id: '',
        research_question: '',
        research_methodology: 'WEB_SEARCH',
      });
    }
  }, [knowledge, open]);

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!formData.title.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Title is required',
        variant: 'destructive',
      });
      return;
    }

    if (!selectedSite?.id) {
      toast({
        title: 'Validation Error',
        description: 'Please select a site first',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);

    try {
      const data: Partial<DomainKnowledge> = {
        site_id: selectedSite.id,
        title: formData.title.trim(),
        knowledge_text: formData.knowledge_text.trim() || undefined,
        knowledge_type: formData.knowledge_type,
        scope_level: formData.scope_level,
        research_question: formData.research_question.trim() || undefined,
        research_methodology: formData.research_methodology,
      };

      // Add scope-specific fields
      switch (formData.scope_level) {
        case 'sector':
          if (formData.scope_sector_id) {
            data.scope_sector_id = parseInt(formData.scope_sector_id, 10);
          }
          break;
        case 'category':
          data.scope_category_path = formData.scope_category_path || undefined;
          break;
        case 'brand':
          data.scope_brand = formData.scope_brand || undefined;
          break;
        case 'attribute':
          data.scope_attribute_name = formData.scope_attribute_name || undefined;
          break;
        case 'product':
          data.scope_product_id = formData.scope_product_id || undefined;
          break;
      }

      let response;
      if (isEdit && knowledge) {
        response = await updateKnowledge(knowledge.id, data);
      } else {
        response = await createKnowledge(data);
      }

      if (response.success) {
        toast({
          title: 'Success',
          description: isEdit ? 'Knowledge updated successfully' : 'Knowledge created successfully',
        });
        onSaved();
        onClose();
      } else {
        toast({
          title: 'Error',
          description: response.error || 'Failed to save knowledge',
          variant: 'destructive',
        });
      }
    } catch (err) {
      console.error('[KnowledgeForm] Error saving:', err);
      toast({
        title: 'Error',
        description: 'Failed to save knowledge',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const getScopeInput = () => {
    switch (formData.scope_level) {
      case 'sector':
        return (
          <div>
            <Label htmlFor="scope_sector_id">Sector ID</Label>
            <Input
              id="scope_sector_id"
              type="number"
              placeholder="e.g., 1"
              value={formData.scope_sector_id}
              onChange={(e) => handleChange('scope_sector_id', e.target.value)}
            />
          </div>
        );
      case 'category':
        return (
          <div>
            <Label htmlFor="scope_category_path">Category Path</Label>
            <Input
              id="scope_category_path"
              placeholder="e.g., Electronics > Phones"
              value={formData.scope_category_path}
              onChange={(e) => handleChange('scope_category_path', e.target.value)}
            />
          </div>
        );
      case 'brand':
        return (
          <div>
            <Label htmlFor="scope_brand">Brand Name</Label>
            <Input
              id="scope_brand"
              placeholder="e.g., Apple"
              value={formData.scope_brand}
              onChange={(e) => handleChange('scope_brand', e.target.value)}
            />
          </div>
        );
      case 'attribute':
        return (
          <div>
            <Label htmlFor="scope_attribute_name">Attribute Name</Label>
            <Input
              id="scope_attribute_name"
              placeholder="e.g., screen_size"
              value={formData.scope_attribute_name}
              onChange={(e) => handleChange('scope_attribute_name', e.target.value)}
            />
          </div>
        );
      case 'product':
        return (
          <div>
            <Label htmlFor="scope_product_id">Product ID</Label>
            <Input
              id="scope_product_id"
              placeholder="UUID of the product"
              value={formData.scope_product_id}
              onChange={(e) => handleChange('scope_product_id', e.target.value)}
            />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Knowledge' : 'Add Knowledge'}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? 'Update the domain knowledge entry'
              : 'Create a new domain knowledge entry for content generation'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Title */}
          <div>
            <Label htmlFor="title">Title *</Label>
            <Input
              id="title"
              placeholder="Brief descriptive title"
              value={formData.title}
              onChange={(e) => handleChange('title', e.target.value)}
            />
          </div>

          {/* Knowledge Type */}
          <div>
            <Label htmlFor="knowledge_type">Knowledge Type</Label>
            <Select
              value={formData.knowledge_type}
              onValueChange={(v) => handleChange('knowledge_type', v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                {KNOWLEDGE_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>
                    <div>
                      <div className="font-medium">{t.label}</div>
                      <div className="text-xs text-muted-foreground">{t.description}</div>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Scope Level */}
          <div>
            <Label htmlFor="scope_level">Scope Level</Label>
            <Select
              value={formData.scope_level}
              onValueChange={(v) => handleChange('scope_level', v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select scope" />
              </SelectTrigger>
              <SelectContent>
                {SCOPE_LEVELS.map((s) => (
                  <SelectItem key={s.value} value={s.value}>
                    <div>
                      <div className="font-medium">{s.label}</div>
                      <div className="text-xs text-muted-foreground">{s.description}</div>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Scope-specific input */}
          {getScopeInput()}

          {/* Knowledge Text */}
          <div>
            <Label htmlFor="knowledge_text">Knowledge Content</Label>
            <Textarea
              id="knowledge_text"
              placeholder="The actual knowledge or rule to apply (can be populated by research)"
              rows={4}
              value={formData.knowledge_text}
              onChange={(e) => handleChange('knowledge_text', e.target.value)}
            />
            <p className="text-xs text-muted-foreground mt-1">
              Leave empty if you want research to populate this
            </p>
          </div>

          {/* Research Question */}
          <div>
            <Label htmlFor="research_question">Research Question</Label>
            <Textarea
              id="research_question"
              placeholder="What question should be researched to fill this knowledge?"
              rows={2}
              value={formData.research_question}
              onChange={(e) => handleChange('research_question', e.target.value)}
            />
          </div>

          {/* Research Methodology */}
          <div>
            <Label htmlFor="research_methodology">Research Methodology</Label>
            <Select
              value={formData.research_methodology}
              onValueChange={(v) => handleChange('research_methodology', v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select methodology" />
              </SelectTrigger>
              <SelectContent>
                {RESEARCH_METHODOLOGIES.map((m) => (
                  <SelectItem key={m.value} value={m.value}>
                    {m.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={loading}>
            {loading && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
            {isEdit ? 'Update' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
